/**
 * Epic 278 Phase 2 Slice D — disposition surface parity notes.
 * Drawer owns primary pattern-level disposition; /incidents remains fallback
 * until media and observation-level review reach parity.
 */

export type DispositionParityStatus = 'in_drawer' | 'fallback_required';

export type DispositionParityItem = {
  id: string;
  status: DispositionParityStatus;
  label: string;
  detail: string;
};

export type DispositionParityNotes = {
  primarySurfaceLabel: string;
  fallbackSurfaceLabel: string;
  fallbackCtaLabel: string;
  summary: string;
  items: DispositionParityItem[];
};

export function buildDispositionParityNotes(): DispositionParityNotes {
  return {
    primarySurfaceLabel: 'Attention drawer',
    fallbackSurfaceLabel: '/incidents full review',
    fallbackCtaLabel: 'Review Evidence',
    summary:
      'Primary disposition is recorded in this drawer. Use Review Evidence only when full media or observation-level review is still required.',
    items: [
      {
        id: 'create_safety_case',
        status: 'in_drawer',
        label: 'Create Safety Case',
        detail: 'Audited create with confirmation, title, owner, and optional rationale.',
      },
      {
        id: 'not_actionable',
        status: 'in_drawer',
        label: 'Not actionable',
        detail: 'Audited close with reason category, reviewer note, and reviewer identity.',
      },
      {
        id: 'needs_more_evidence',
        status: 'in_drawer',
        label: 'Needs more evidence',
        detail: 'Audited deferral with reason and optional evidence request / review date.',
      },
      {
        id: 'link_existing',
        status: 'in_drawer',
        label: 'Link existing Safety Case',
        detail: 'Audited already-managed decision when matching open cases are available.',
      },
      {
        id: 'representative_media',
        status: 'fallback_required',
        label: 'Representative media and episode playback',
        detail: 'Thumbnails, clips, and episode review remain on /incidents until drawer media loads.',
      },
      {
        id: 'observation_level',
        status: 'fallback_required',
        label: 'Observation-level disposition',
        detail: 'Per-observation true/false/expected decisions stay in full Risk Pattern review.',
      },
      {
        id: 'managed_case_ops',
        status: 'fallback_required',
        label: 'Managed-case Scan operations',
        detail: 'Report preparation, continue review, and schedule configuration stay on /incidents.',
      },
      {
        id: 'missing_representative',
        status: 'fallback_required',
        label: 'Missing representative evidence',
        detail: 'Non-create dispositions require a representative incident; finish those in Review Evidence.',
      },
    ],
  };
}

export function dispositionParityInDrawer(notes: DispositionParityNotes = buildDispositionParityNotes()): DispositionParityItem[] {
  return notes.items.filter((item) => item.status === 'in_drawer');
}

export function dispositionParityFallbackRequired(
  notes: DispositionParityNotes = buildDispositionParityNotes(),
): DispositionParityItem[] {
  return notes.items.filter((item) => item.status === 'fallback_required');
}
